import * as api from "../../../../api/generated-api";
import {
  DealDocument,
  DealDocumentSignature,
  DealDocumentType,
} from "../../../../api/generated-api";
import { toValidIdentifier } from "../../../../helpers/utilsTS";
import {
  Box,
  ListItemIcon,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DocumentDownloadIcon from "../../../common/icons/document/documentDownloadIcon";
import RemoveIcon from "../../../common/icons/removeIcon";
import React, {
  ChangeEvent,
  PropsWithChildren,
  ReactElement,
  useState,
} from "react";
import { DealDocumentRowItemActionsMenu } from "./DealDocumentRowItemActionsMenu";
import Button from "../../../common/forms/button";
import SignatureIcon from "../../../common/icons/signatureIcon";
import { styled, SxProps, Theme, useTheme } from "@mui/material/styles";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import { DKChip } from "../../../common/DKChip";
import { ProgressIndicator } from "../../../common/progress/ProgressIndicator";
import { UploadDealDocumentFilePicker } from "../UploadDealDocumentFilePicker";
import { DealDocumentRow } from "./DealDocumentRow";
import EditIcon from "@mui/icons-material/Edit";

export const DealDocumentRowContainer = ({
  children,
}: {
  children: ReactElement | ReactElement[];
}) => {
  const container: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    flex: 1,
  };

  return (
    <Box id="dealDocumentRowContainer" sx={container}>
      {children}
    </Box>
  );
};

export const DealDocumentRowIconContainer = ({
  children,
}: {
  children: ReactElement | ReactElement[];
}) => {
  const iconContainer: SxProps<Theme> = {
    display: "flex",
    minWidth: "30px",
    marginBottom: "5px",
  };

  return <Box sx={iconContainer}>{children}</Box>;
};

export const DealDocumentRowLeftContainer = ({
  children,
}: {
  children: ReactElement | ReactElement[];
}) => {
  const leftContainer: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    paddingLeft: "10px",
    flex: 1,
  };
  return <Box sx={leftContainer}>{children}</Box>;
};

export const DealDocumentRowRightContainer = ({
  children,
}: PropsWithChildren<Record<never, never>>) => {
  const rightContainer: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    alignItems: "center",
    justifyContent: "flex-end",
  };
  return <Box sx={rightContainer}>{children}</Box>;
};

export const DealDocumentRowActionContainer = ({
  children,
}: PropsWithChildren<Record<never, never>>) => {
  const actionContainer: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  };
  return <Box sx={actionContainer}>{children}</Box>;
};

export const StyledListItemIcon = styled(ListItemIcon)({
  display: "flex",
  justifyContent: "center",
  minWidth: "30px",
  paddingRight: "8px",
});

export type DealDocumentRowItemProps = {
  orgId: number;
  dealId: number;
  documentType: DealDocumentType;
  dealDocument: DealDocument;
  deletable: boolean;
  uploadable: boolean;
  dealDocumentSignatures: DealDocumentSignature[];
  onDocumentDeleteClick: (documentIdToDelete: number) => void;
  onDocumentVersionUploadFileSelected: (
    event: ChangeEvent<HTMLInputElement>,
    documentId: number
  ) => Promise<DealDocument>;
  onDocumentUploadFileCancel: () => void;
  onDocumentDownloadClick: (documentId: number) => void;
  onDocumentSignersClick: (dealDocument: DealDocument) => void;
  postNameAdornment?: ReactElement;
  extraRowAction?: ReactElement;
  highlighted?: boolean;
};

export const DealDocumentRowItem = ({
  orgId,
  dealId,
  documentType,
  dealDocument,
  deletable,
  uploadable,
  onDocumentDeleteClick,
  onDocumentVersionUploadFileSelected,
  onDocumentUploadFileCancel,
  onDocumentSignersClick,
  onDocumentDownloadClick,
  postNameAdornment,
  extraRowAction,
  highlighted = false,
  dealDocumentSignatures,
}: DealDocumentRowItemProps) => {
  const [showProgressIndicator, setShowProgressIndicator] =
    React.useState<boolean>(false);
  const [showUploadFileVersionPicker, setShowUploadFileVersionPicker] =
    React.useState<boolean>(false);
  const [selected, setSelected] = useState(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const theme = useTheme();

  const enabledMenuText: SxProps<Theme> = {
    color: theme.palette.secondary.dark,
  };

  const chipContainer: SxProps<Theme> = {
    marginRight: "10px",
    height: "25px",
  };

  const highlightRow = () => {
    //highlights the row for a short period of time; ex after a new version is uploaded
    setSelected(true);
    setTimeout(() => {
      setSelected(false);
    }, 600);
  };

  const Chips = () => {
    return (
      <>
        {dealDocument && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Box sx={chipContainer}>
              <DKChip
                label={`version ${dealDocument.version}`}
                data-testid={`version-badge-${toValidIdentifier(
                  documentType
                )}-version-${dealDocument.version}`}
              />
            </Box>
            {!!postNameAdornment && (
              <Box sx={chipContainer}>{postNameAdornment}</Box>
            )}
          </Box>
        )}
      </>
    );
  };

  const isSmallScreen = useMediaQuery(
    `(max-width:${theme.breakpoints.values.lg}px)`
  );

  return (
    <Box
      data-testid="deal-document-row"
      style={{
        backgroundColor: selected ? theme.palette.primary.light : "transparent",
        transition: "background-color 0.5s ease",
      }}
    >
      <Box
        key={toValidIdentifier(documentType)}
        sx={{
          backgroundColor: highlighted
            ? theme.palette.secondary.light
            : "transparent",
          padding: highlighted ? "10px 0px" : "0px",
        }}
      >
        {dealDocument && (
          <>
            <DealDocumentRow
              orgId={orgId}
              dealId={dealId}
              dealDocumentId={dealDocument.id}
              documentName={dealDocument.name}
              documentLinkPath={`/${orgId}/deals/${dealId}/documents/${dealDocument.id}`}
              documentNameAdornment={<Chips />}
              highlightRow={highlightRow}
              editMode={editMode}
              setEditMode={setEditMode}
            >
              <DealDocumentRowActionContainer>
                <Box
                  id="dealDocumentRowActionContainer"
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  {showProgressIndicator && (
                    <ProgressIndicator
                      size={20}
                      dataTestId={`uploading-status-${toValidIdentifier(
                        documentType
                      )}-icon`}
                    />
                  )}
                  <UploadDealDocumentFilePicker
                    visible={showUploadFileVersionPicker}
                    dataTestId={`${toValidIdentifier(
                      documentType
                    )}-document-version-picker`}
                    documentType={documentType}
                    onDocumentSelect={async (event) => {
                      const uploadedDocument =
                        await onDocumentVersionUploadFileSelected(
                          event,
                          dealDocument.id
                        );
                      return uploadedDocument;
                    }}
                    onDocumentSelected={() => {
                      setShowProgressIndicator(true);
                    }}
                    onDocumentFinishedUploading={() => {
                      setShowProgressIndicator(false);
                      setShowUploadFileVersionPicker(false);
                      highlightRow();
                    }}
                    onCancel={onDocumentUploadFileCancel}
                  />

                  {!!extraRowAction && !isSmallScreen && (
                    <Box
                      sx={{
                        marginLeft: "10px",
                        marginBottom: isSmallScreen ? "10px" : "0px",
                      }}
                    >
                      {extraRowAction}
                    </Box>
                  )}
                  {dealDocument &&
                    dealDocumentSignatures?.length === 0 &&
                    !highlighted &&
                    documentType !==
                      api.DealDocumentType
                        .Filed_Certificate_of_Incorporation && (
                      <Box>
                        <Box
                          sx={{
                            marginLeft: "10px",
                          }}
                        >
                          <Button
                            data-testid={`add-signers-button`}
                            type="secondary"
                            size="small"
                            onClick={() => {
                              onDocumentSignersClick(dealDocument);
                            }}
                          >
                            Assign Signers
                          </Button>
                        </Box>
                      </Box>
                    )}
                </Box>
              </DealDocumentRowActionContainer>
              <DealDocumentRowItemActionsMenu
                dataTestId={`open-${toValidIdentifier(documentType)}-menu`}
              >
                {dealDocument && !editMode && (
                  <MenuItem onClick={() => setEditMode(true)}>
                    <StyledListItemIcon>
                      <EditIcon
                        data-testid={`edit-name-${toValidIdentifier(
                          documentType
                        )}`}
                        sx={{ cursor: "pointer" }}
                        width={"20px"}
                        height={"20px"}
                        htmlColor={theme.palette.secondary.dark}
                      />
                    </StyledListItemIcon>
                    <Typography sx={enabledMenuText}>Edit</Typography>
                  </MenuItem>
                )}

                {dealDocument &&
                  dealDocumentSignatures?.length > 0 &&
                  documentType !==
                    api.DealDocumentType.Filed_Certificate_of_Incorporation && (
                    <MenuItem
                      onClick={() => onDocumentSignersClick(dealDocument)}
                    >
                      <StyledListItemIcon>
                        <SignatureIcon
                          data-testid={`edit-signers-${toValidIdentifier(
                            documentType
                          )}-button`}
                          fill={theme.palette.secondary.dark}
                        />
                      </StyledListItemIcon>
                      <Typography sx={enabledMenuText}>Signers</Typography>
                    </MenuItem>
                  )}
                {dealDocument && uploadable && (
                  <MenuItem
                    onClick={() => {
                      setShowUploadFileVersionPicker(true);
                    }}
                  >
                    <StyledListItemIcon>
                      <LibraryAddOutlinedIcon
                        style={{ color: theme.palette.secondary.dark }}
                        data-testid={`new-version-${toValidIdentifier(
                          documentType
                        )}-button`}
                      />
                    </StyledListItemIcon>
                    <Typography sx={enabledMenuText}>New Version</Typography>
                  </MenuItem>
                )}
                {dealDocument && !uploadable && (
                  <MenuItem onClick={undefined}>
                    <StyledListItemIcon>
                      <LibraryAddOutlinedIcon
                        style={{ color: theme.palette.tertiary.main }}
                        data-testid={`new-version-${toValidIdentifier(
                          documentType
                        )}-button-disabled`}
                      />
                    </StyledListItemIcon>
                    <Typography variant="inherit">New Version</Typography>
                  </MenuItem>
                )}
                {dealDocument && deletable && (
                  <MenuItem
                    onClick={() => {
                      onDocumentDeleteClick(dealDocument.id);
                    }}
                  >
                    <StyledListItemIcon>
                      <RemoveIcon
                        data-testid={`remove-${toValidIdentifier(
                          documentType
                        )}-button`}
                        className={"row-action-icon"}
                        fill={theme.palette.secondary.dark}
                      />
                    </StyledListItemIcon>
                    <Typography sx={enabledMenuText}>Delete</Typography>
                  </MenuItem>
                )}
                {(!dealDocument || !deletable) && (
                  <MenuItem>
                    <StyledListItemIcon>
                      <RemoveIcon
                        data-testid={`remove-${toValidIdentifier(
                          documentType
                        )}-button-disabled`}
                        className={"row-action-icon"}
                        fill={theme.palette.tertiary.main}
                        onClick={undefined}
                      />
                    </StyledListItemIcon>
                    <Typography variant="inherit">Delete</Typography>
                  </MenuItem>
                )}
                {dealDocument && (
                  <MenuItem
                    onClick={() => {
                      onDocumentDownloadClick(dealDocument.id);
                    }}
                  >
                    <StyledListItemIcon>
                      <DocumentDownloadIcon
                        data-testid={`download-${toValidIdentifier(
                          documentType
                        )}-button`}
                        className={"row-action-icon"}
                      />
                    </StyledListItemIcon>
                    <Typography sx={enabledMenuText}>Download</Typography>
                  </MenuItem>
                )}
              </DealDocumentRowItemActionsMenu>
            </DealDocumentRow>
          </>
        )}
      </Box>
    </Box>
  );
};
